import React from 'react';
import { Table } from 'antd';
import type { TableColumnsType } from 'antd';
import { WmsProgressByPreviousStationType } from '../../graphql/queries/getWmsProgressByPreviousStation';
import { useTranslation } from 'react-i18next';

// 定义本页面的统计的状态类型 总数 ｜ 未打包数量 ｜ 已打包数量 ｜ 异常数量 ｜ 需退回数量
export type OrderStatus = 'total' | 'unPackNum' | 'packNum' | 'abnormalNum' | 'returnNum';

interface ByPreviousStationTableProps {
  progressData: WmsProgressByPreviousStationType[];
  loading?: boolean;
  onCellClick?: (sortingCode: string, status: OrderStatus, level: string) => void;
}

interface ExpandedRowData {
  sortingCode: string;
  key: string;
  previousHub?: string;
  previousWhs?: string;
  total: number;
  unPackNum: number;
  packNum: number;
  abnormalNum: number;
  returnNum: number;
  level: string;
  children?: ExpandedRowData[];
}

export const ByPreviousStationTable: React.FC<ByPreviousStationTableProps> = ({
  progressData,
  loading,
  onCellClick
}) => {
  const { t } = useTranslation();
  
  // 构建表格数据，包含展开的子级数据
  const buildTableData = (data: WmsProgressByPreviousStationType[], level: number = 0): ExpandedRowData[] => {
    const result: ExpandedRowData[] = [];
    
    data.forEach(item => {
      // 如果previousHub为"N/A"，则不显示children（直接展示hub和whs都为N/A的数据）
      const shouldShowChildren = item.previousHub !== "N/A" && item.children && item.children.length > 0;
      
      const rowData: ExpandedRowData = {
        sortingCode: item.sortingCode,
        key: item.key,
        previousHub: item.previousHub,
        previousWhs: item.previousWhs,
        total: item.total,
        unPackNum: item.unPackNum,
        packNum: item.packNum,
        abnormalNum: item.abnormalNum,
        returnNum: item.returnNum,
        level: item.level,
        children: shouldShowChildren ? buildTableData(item.children, level + 1) : undefined
      };
      result.push(rowData);
    });
    
    return result;
  };
  const tableData = buildTableData(progressData);

  // 创建可点击数字单元格
  const renderClickableNumber = (value: number, record: ExpandedRowData, status: OrderStatus) => {
    const displayValue = value || 0;
    
    if (displayValue === 0 || !onCellClick) {
      return <span>{displayValue}</span>;
    }

    // 根据层级构造正确的sortingCode
    const getSortingCodeForLevel = (record: ExpandedRowData): string => {
      switch (record.level) {
        case 'HUB':
          // HUB级别：使用previousHub作为sortingCode
          return record.previousHub || record.sortingCode;
        case 'WHS':
          // WHS级别：使用previousHub和previousWhs组合
          if (record.previousHub && record.previousWhs && record.previousWhs !== 'N/A') {
            return `${record.previousHub}-${record.previousWhs}`;
          }
          return record.previousHub || record.sortingCode;
        default:
          return record.sortingCode;
      }
    };

    return (
      <span
        className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline font-medium"
        onClick={(e) => {
          e.stopPropagation();
          const fullSortingCode = getSortingCodeForLevel(record);
          onCellClick(fullSortingCode, status, record.level);
        }}
        title={`点击查看${status === 'total' ? '全部' : status === 'unPackNum' ? '未装袋' : status === 'packNum' ? '已装袋' : status === 'abnormalNum' ? '异常' : '待退回'}订单`}
      >
        {displayValue}
      </span>
    );
  };

  const columns: TableColumnsType<ExpandedRowData> = [
    {
      title: t('wms.progress.columns.previousHUB'),
      dataIndex: 'previousHub',
      key: 'previousHub',
      width: 120,
      align: 'left',
      render: (text, record) => {
        if (record.level === 'SUMMARY') {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {text || 'All'}
            </div>
          );
        }
        if (record.level === 'HUB') {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {text || 'N/A'}
            </div>
          );
        }
        // WHS级别时，Previous HUB显示空白
        if (record.level === 'WHS') {
          return '';
        }
        return text || 'N/A';
      },
    },
    {
      title: t('wms.progress.columns.previousWHS'),
      dataIndex: 'previousWhs',
      key: 'previousWhs',
      width: 120,
      align: 'left',
      render: (text, record) => {
        if (record.level === 'SUMMARY') {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {text || 'All'}
            </div>
          );
        }
        if (record.level === 'HUB') {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {text || 'All'}
            </div>
          );
        }
        if (record.level === 'WHS') {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {text || 'N/A'}
            </div>
          );
        }
        return text || 'N/A';
      },
    },
    {
      title: t('wms.progress.columns.totalInHub'),
      dataIndex: 'total',
      key: 'total',
      width: 100,
      align: 'left',
      render: (text, record) => renderClickableNumber(text, record, 'total'),
    },
    {
      title: t('wms.progress.columns.unbagged'),
      dataIndex: 'unPackNum',
      key: 'unPackNum',
      width: 100,
      align: 'left',
      render: (text, record) => renderClickableNumber(text, record, 'unPackNum'),
    },
    {
      title: t('wms.progress.columns.bagged'),
      dataIndex: 'packNum',
      key: 'packNum',
      width: 100,
      align: 'left',
      render: (text, record) => renderClickableNumber(text, record, 'packNum'),
    },
    {
      title: t('wms.progress.columns.exception'),
      dataIndex: 'abnormalNum',
      key: 'abnormalNum',
      width: 100,
      align: 'left',
      render: (text, record) => renderClickableNumber(text, record, 'abnormalNum'),
    },
    {
      title: t('wms.progress.columns.toReturn'),
      dataIndex: 'returnNum',
      key: 'returnNum',
      width: 100,
      align: 'left',
      render: (text, record) => renderClickableNumber(text, record, 'returnNum'),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={tableData}
      rowKey="key"
      loading={loading}
      pagination={false}
      size="middle"
      className="mb-4"
      rowClassName={(record) => {
        if (record.level === 'SUMMARY') return 'bg-blue-50 font-bold';
        return '';
      }}
      expandable={{
        defaultExpandAllRows: false,
        childrenColumnName: 'children',
        rowExpandable: (record) => record.level !== 'SUMMARY' && !!record.children && record.children.length > 0
      }}
    />
  );
};

export default ByPreviousStationTable;

